import { useEffect, useState } from 'react'
import { Menu, X, Smartphone } from 'lucide-react'
import { AppleLogo } from './AppleLogo'
import {
  ANDROID_URL,
  IOS_APPSTORE_URL,
  detectPlatform,
  trackDownload,
  type DevicePlatform,
} from '../lib/appLinks'

const links = [
  { href: '#products', label: 'Products' },
  { href: '#features', label: 'Features' },
  { href: '#fuzzy', label: 'Fuzzy Search' },
  { href: '#tester', label: 'Become a Tester' },
]

/** Platform-aware primary CTA: both stores are LIVE → straight to the right
 *  store (one click); desktop → Play (the default). */
function getCta(platform: DevicePlatform): {
  label: string
  href: string
  action: 'android' | 'ios'
  icon: JSX.Element
} {
  if (platform === 'ios') {
    return { label: 'App Store', href: IOS_APPSTORE_URL, action: 'ios', icon: <AppleLogo size={15} /> }
  }
  return {
    label: platform === 'android' ? 'Download' : 'Get the app',
    href: ANDROID_URL,
    action: 'android',
    icon: <Smartphone size={16} />,
  }
}

export function Header() {
  const [open, setOpen] = useState(false)
  const [platform, setPlatform] = useState<DevicePlatform>('desktop')

  useEffect(() => {
    setPlatform(detectPlatform())
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const cta = getCta(platform)

  return (
    <header className="glass-chrome sticky top-0 z-40">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
        <a href="#top" className="flex items-center" aria-label="BimaUncle">
          <img
            src="/bimauncle-top-bar-logo.png"
            alt="BimaUncle"
            className="h-10 w-auto"
          />
        </a>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-pill px-3 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-white/60 hover:text-ink"
            >
              {l.label}
            </a>
          ))}
          <a
            href={cta.href}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackDownload(cta.action)}
            className="btn-primary ml-3 !h-10 !px-5 text-sm"
          >
            {cta.icon}
            {cta.label}
          </a>
        </nav>

        <button
          aria-label="Open menu"
          aria-expanded={open}
          className="rounded-pill p-2 md:hidden"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden">
          <div className="mx-4 mb-3 rounded-card border border-[var(--color-divider)] bg-white p-3 shadow-card">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block rounded-input px-4 py-3 text-sm font-medium text-ink hover:bg-[var(--color-nav)]"
              >
                {l.label}
              </a>
            ))}
            <a
              href={cta.href}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                trackDownload(cta.action)
                setOpen(false)
              }}
              className="btn-primary mt-2 w-full !h-11 text-sm"
            >
              {cta.icon}
              {cta.label}
            </a>
          </div>
        </div>
      )}
    </header>
  )
}
